import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material'
import { type ReactElement, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ClientSelector from 'components/client-selector/index'
import { fetchFieldsByFarm } from 'api/field'
import { fetchHarvestsByField } from 'api/harvest'
import { TEST_IDS } from 'test-ids'

import { type ClientModel } from 'state/client'
import { type FarmOptionModel } from 'state/farm'

import { type FieldOption, type HarvestOption } from './types'

export type HarvestPickerProps = {
  onChange: (harvestId: number | undefined) => void
}

export default function HarvestPicker(props: HarvestPickerProps): ReactElement {
  const { t } = useTranslation()

  const [selectedClient, setSelectedClient] = useState<ClientModel | null>(null)
  const [selectedFarm, setSelectedFarm] = useState<FarmOptionModel | null>(null)
  const [selectedField, setSelectedField] = useState<FieldOption | null>(null)
  const [selectedHarvest, setSelectedHarvest] = useState<HarvestOption | null>(
    null,
  )

  const [fieldOptions, setFieldOptions] = useState<FieldOption[]>([])
  const [fieldsLoading, setFieldsLoading] = useState(false)
  const [fieldsLoadedForFarmId, setFieldsLoadedForFarmId] = useState<
    number | null
  >(null)

  const [harvestOptions, setHarvestOptions] = useState<HarvestOption[]>([])
  const [harvestsLoading, setHarvestsLoading] = useState(false)
  const [harvestsLoadedForFieldId, setHarvestsLoadedForFieldId] = useState<
    number | null
  >(null)

  // Track the current farm/field selection in refs so an in-flight
  // request can check, at resolution time, whether it's still relevant -
  // overlapping requests (e.g. opening the Field dropdown for one farm,
  // then quickly switching farms) can otherwise resolve out of order and
  // let a superseded response overwrite the current selection's options.
  const selectedFarmIdRef = useRef<number | null>(null)
  const selectedFieldIdRef = useRef<number | null>(null)

  useEffect(() => {
    selectedFarmIdRef.current = selectedFarm?.id ?? null
  }, [selectedFarm])

  useEffect(() => {
    selectedFieldIdRef.current = selectedField?.id ?? null
  }, [selectedField])

  const loadFieldOptions = async (farmId: number) => {
    if (fieldsLoadedForFarmId === farmId) return
    setFieldsLoading(true)
    try {
      const res = await fetchFieldsByFarm(farmId)
      if (selectedFarmIdRef.current !== farmId) return
      setFieldOptions(res.data.results || res.data || [])
      setFieldsLoadedForFarmId(farmId)
    } finally {
      if (selectedFarmIdRef.current === farmId) setFieldsLoading(false)
    }
  }

  const loadHarvestOptions = async (fieldId: number) => {
    if (harvestsLoadedForFieldId === fieldId) return
    setHarvestsLoading(true)
    try {
      const res = await fetchHarvestsByField(fieldId)
      if (selectedFieldIdRef.current !== fieldId) return
      setHarvestOptions(res.data || [])
      setHarvestsLoadedForFieldId(fieldId)
    } finally {
      if (selectedFieldIdRef.current === fieldId) setHarvestsLoading(false)
    }
  }

  const onClientChange = (_event: any, newValue: ClientModel | null) => {
    setSelectedClient(newValue)
    setSelectedFarm(null)
    setSelectedField(null)
    setSelectedHarvest(null)
    setFieldOptions([])
    setFieldsLoadedForFarmId(null)
    setHarvestOptions([])
    setHarvestsLoadedForFieldId(null)
    props.onChange(undefined)
  }

  const onFarmChange = (_event: any, newValue: FarmOptionModel | null) => {
    setSelectedFarm(newValue)
    setSelectedField(null)
    setSelectedHarvest(null)
    setFieldOptions([])
    setFieldsLoadedForFarmId(null)
    setHarvestOptions([])
    setHarvestsLoadedForFieldId(null)
    props.onChange(undefined)
  }

  const onFieldChange = (_event: any, newValue: FieldOption | null) => {
    setSelectedField(newValue)
    setSelectedHarvest(null)
    setHarvestOptions([])
    setHarvestsLoadedForFieldId(null)
    props.onChange(undefined)
  }

  const onHarvestChange = (_event: any, newValue: HarvestOption | null) => {
    setSelectedHarvest(newValue)
    props.onChange(newValue?.id)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-end',
        my: 1,
      }}
    >
      <Box sx={{ minWidth: 200 }}>
        <ClientSelector
          selectedClient={selectedClient}
          onChange={onClientChange}
          compact
        />
      </Box>

      <Stack spacing={0.5} sx={{ minWidth: 200 }}>
        <Typography variant="caption">{t('farms')}</Typography>
        <Autocomplete
          size="small"
          data-testid={TEST_IDS.trxStreamFarmSelect}
          options={selectedClient?.farms ?? []}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedFarm}
          disabled={!selectedClient}
          onChange={onFarmChange}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('farms')} />
          )}
        />
      </Stack>

      <Stack spacing={0.5} sx={{ minWidth: 200 }}>
        <Typography variant="caption">{t('select_field')}</Typography>
        <Autocomplete
          size="small"
          data-testid={TEST_IDS.trxStreamFieldSelect}
          options={fieldOptions}
          loading={fieldsLoading}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedField}
          disabled={!selectedFarm}
          onOpen={() => {
            if (selectedFarm) loadFieldOptions(selectedFarm.id)
          }}
          onChange={onFieldChange}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('select_field')} />
          )}
        />
      </Stack>

      <Stack spacing={0.5} sx={{ minWidth: 220 }}>
        <Typography variant="caption">{t('select_harvest')}</Typography>
        <Autocomplete
          size="small"
          data-testid={TEST_IDS.trxStreamHarvestSelect}
          options={harvestOptions}
          loading={harvestsLoading}
          getOptionLabel={(option) =>
            option
              ? `${option.cropYear} (${option.startDate} – ${option.endDate})`
              : ''
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedHarvest}
          disabled={!selectedField}
          onOpen={() => {
            if (selectedField) loadHarvestOptions(selectedField.id)
          }}
          onChange={onHarvestChange}
          renderInput={(params) => (
            <TextField {...params} placeholder={t('select_harvest')} />
          )}
        />
      </Stack>
    </Box>
  )
}
